import React, { useState, useEffect } from 'react';
import { employeeApi, extractErrorMessage } from '../services/hrApi';
import type { Employee } from '../types';
import OnboardingWizard from './Onboarding/OnboardingWizard';
import './PageStyles.scss';
import './Onboarding.scss';

const Onboarding: React.FC = () => {
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [showWizard, setShowWizard] = useState<boolean>(false);
  const [selectedEmployeeId, setSelectedEmployeeId] = useState<string | undefined>(undefined);
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
  const [employeeToDelete, setEmployeeToDelete] = useState<Employee | null>(null);
  const [deleting, setDeleting] = useState<boolean>(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [openActionMenuId, setOpenActionMenuId] = useState<string | null>(null);

  useEffect(() => {
    if (!showWizard) {
      fetchOnboardingEmployees();
    }
  }, [showWizard]);

  const fetchOnboardingEmployees = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await employeeApi.getEmployees({ status: 'onboarding' });
      // Handle both array response and object with results array
      const employeesList = Array.isArray(data) ? data : (data.results || data.employees || []);
      setEmployees(employeesList);
    } catch (err: any) {
      console.error('Error fetching onboarding employees:', err);
      setError(err?.message || 'Failed to load onboarding employees');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch {
      return dateString;
    }
  };

  const handleActionMenuToggle = (e: React.MouseEvent, employeeId: string) => {
    e.stopPropagation(); // Prevent card click
    setOpenActionMenuId(openActionMenuId === employeeId ? null : employeeId);
  };

  const handleDeleteClick = (e: React.MouseEvent, employee: Employee) => {
    e.stopPropagation(); // Prevent card click
    setOpenActionMenuId(null); // Close menu
    setEmployeeToDelete(employee);
    setShowDeleteModal(true);
    setDeleteError(null);
  };

  // Close action menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      // Don't close if clicking inside an action menu
      if (!target.closest('.action-menu-wrapper')) {
        setOpenActionMenuId(null);
      }
    };
    if (openActionMenuId) {
      // Use setTimeout to avoid immediate closure when opening
      setTimeout(() => {
        document.addEventListener('click', handleClickOutside);
      }, 0);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [openActionMenuId]);

  const handleDeleteConfirm = async () => {
    if (!employeeToDelete) return;

    setDeleting(true);
    setDeleteError(null);

    try {
      await employeeApi.deleteEmployee(employeeToDelete.id);
      setShowDeleteModal(false);
      setEmployeeToDelete(null);
      // Refresh the list
      await fetchOnboardingEmployees();
    } catch (err: any) {
      console.error('Error deleting employee:', err);
      const errorMessage = extractErrorMessage(err);
      setDeleteError(errorMessage || 'Failed to delete employee. Please try again.');
    } finally {
      setDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteModal(false);
    setEmployeeToDelete(null);
    setDeleteError(null);
  };

  return (
    <div className="hr-portal-page">
      <div className="hr-portal-page-header">
        <div className="page-header-top">
          <div>
            <h2>Onboarding</h2>
            <p className="page-description">Manage employee onboarding process and progress</p>
          </div>
          {!showWizard && (
            <button
              className="btn-onboard-new"
              onClick={() => {
                setSelectedEmployeeId(undefined);
                setShowWizard(true);
              }}
            >
              <i className="bx bx-user-plus"></i>
              Onboard New Employee
            </button>
          )}
          {showWizard && (
            <button
              className="btn-back-to-list"
              onClick={() => {
                setShowWizard(false);
                setSelectedEmployeeId(undefined);
              }}
            >
              <i className="bx bx-arrow-back"></i>
              Back to List
            </button>
          )}
        </div>
      </div>
      <div className="hr-portal-page-content">
        {showWizard ? (
          <OnboardingWizard
            draftEmployeeId={selectedEmployeeId}
            onCancel={() => {
              setShowWizard(false);
              setSelectedEmployeeId(undefined);
            }}
            onComplete={() => {
              setShowWizard(false);
              setSelectedEmployeeId(undefined);
              fetchOnboardingEmployees(); // Refresh the list after completion
            }}
          />
        ) : (
          <>
            {loading && (
          <div className="onboarding-loading">
            <p>Loading onboarding employees...</p>
          </div>
        )}

        {error && (
          <div className="onboarding-error">
            <p>{error}</p>
            <button onClick={fetchOnboardingEmployees} className="btn-retry">
              Retry
            </button>
          </div>
        )}

        {!loading && !error && employees.length === 0 && (
          <div className="onboarding-empty">
            <i className="bx bx-user-plus"></i>
            <p>No employees currently in onboarding</p>
          </div>
        )}

        {!loading && !error && employees.length > 0 && (
          <>
            <div className="onboarding-summary">
              <div className="summary-card">
                <div className="summary-icon">
                  <i className="bx bx-user-plus"></i>
                </div>
                <div className="summary-content">
                  <h3>{employees.length}</h3>
                  <p>Employees in Onboarding</p>
                </div>
              </div>
            </div>

            <div className="onboarding-employees-grid">
              {employees.map((employee) => (
                <div 
                  key={employee.id} 
                  className="onboarding-employee-card clickable"
                  onClick={() => {
                    setSelectedEmployeeId(employee.id);
                    setShowWizard(true);
                  }}
                >
                  <div className="employee-card-header">
                    <div className="employee-avatar">
                      {employee.first_name?.[0]?.toUpperCase() || 'E'}
                      {employee.last_name?.[0]?.toUpperCase() || ''}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span className="status-badge status-onboarding">
                        Onboarding
                      </span>
                      {employee.status === 'onboarding' && (
                        <div className="action-menu-wrapper">
                          <button
                            className="btn-action-menu"
                            onClick={(e) => handleActionMenuToggle(e, employee.id)}
                            title="Actions"
                          >
                            <i className="bx bx-dots-vertical-rounded"></i>
                          </button>
                          {openActionMenuId === employee.id && (
                            <div className="action-menu-dropdown">
                              <button
                                className="action-menu-item action-menu-item-danger"
                                onClick={(e) => handleDeleteClick(e, employee)}
                              >
                                <i className="bx bx-trash"></i>
                                <span>Delete Employee</span>
                              </button>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="employee-card-body">
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                      <h3 className="employee-name">
                        {employee.first_name} {employee.last_name}
                      </h3>
                      <i className="bx bx-chevron-right" style={{ fontSize: '24px', color: '#0dcaf0' }}></i>
                    </div>
                    <p className="employee-id">ID: {employee.employee_id}</p>
                    <div className="employee-info">
                      <div className="info-item">
                        <i className="bx bx-envelope"></i>
                        <span>{employee.email}</span>
                      </div>
                      {employee.phone && (
                        <div className="info-item">
                          <i className="bx bx-phone"></i>
                          <span>{employee.phone}</span>
                        </div>
                      )}
                      <div className="info-item">
                        <i className="bx bx-briefcase"></i>
                        <span>{employee.position}</span>
                      </div>
                      <div className="info-item">
                        <i className="bx bx-building"></i>
                        <span>{employee.department}</span>
                      </div>
                      <div className="info-item">
                        <i className="bx bx-calendar"></i>
                        <span>Hired: {formatDate(employee.hire_date)}</span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
          </>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="delete-modal-overlay" onClick={handleDeleteCancel}>
          <div className="delete-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="delete-modal-header">
              <h3>Delete Employee</h3>
              <button
                className="btn-close-modal"
                onClick={handleDeleteCancel}
                disabled={deleting}
              >
                <i className="bx bx-x"></i>
              </button>
            </div>
            <div className="delete-modal-body">
              <div className="delete-warning-icon">
                <i className="bx bx-error-circle"></i>
              </div>
              <h4>Warning: This action is permanent</h4>
              <p className="delete-warning-text">
                You are about to permanently delete <strong>{employeeToDelete?.first_name} {employeeToDelete?.last_name}</strong> from the onboarding process.
              </p>
              <p className="delete-warning-text">
                <strong>This cannot be undone.</strong> All associated data including onboarding progress, documents, and records will be permanently deleted and cannot be recovered.
              </p>
              {deleteError && (
                <div className="delete-error-message">
                  <i className="bx bx-error-circle"></i>
                  {deleteError}
                </div>
              )}
            </div>
            <div className="delete-modal-footer">
              <button
                className="btn-cancel-delete"
                onClick={handleDeleteCancel}
                disabled={deleting}
              >
                Cancel
              </button>
              <button
                className="btn-confirm-delete"
                onClick={handleDeleteConfirm}
                disabled={deleting}
              >
                {deleting ? (
                  <>
                    <i className="bx bx-loader-alt bx-spin"></i>
                    Deleting...
                  </>
                ) : (
                  <>
                    <i className="bx bx-trash"></i>
                    Delete Permanently
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Onboarding;
